import React, { useState } from 'react';
import { DialogTitle, DialogContent, Box, Button, Typography, TextField, LinearProgress } from '@mui/material';
import { useMutation } from 'react-query';
import { ToastProps, ToastType } from 'components/Common/Toast';
import { inviteFreeTrialProviders } from 'pages/Dashboard/services/freeTrial.service';
import http from 'utils/http';
import Theme from 'theme';
import { trackMixpanelEvent, logSentryError } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';

interface InviteTeamProps {
  onNext: () => void;
  step: number;
  totalSteps: number;
  token?: string;
  setToastProps?: (props: ToastProps | null) => void;
}

function InviteTeam({ onNext, step, totalSteps, token, setToastProps }: InviteTeamProps) {
  const [emails, setEmails] = useState('');

  React.useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_INVITE_TEAM_DIALOG_VIEWED);
  }, []);

  const mutation = useMutation({
    mutationFn: (payload:
      { emails: string[] }) => inviteFreeTrialProviders(http.post, payload, token),
    onError: (error) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_INVITE_TEAM_ERROR);
      logSentryError(`Error sending invites - ${(error as Error)?.message}`);
      setToastProps?.({
        message: 'Failed to send invites. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: () => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_INVITE_TEAM_SUCCESS);
      setToastProps?.({
        message: 'Invite(s) sent successfully!',
        open: true,
        type: ToastType.success,
      });
      if (onNext) onNext();
    },
  });

  const handleSend = () => {
    const emailList = emails.split(/[,\s]+/).filter(Boolean);
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_INVITE_TEAM_SEND_INVITES_BUTTON_CLICKED, {
      emailList: JSON.stringify(emailList),
    });
    if (emailList.length === 0) {
      setToastProps?.({
        message: 'Please enter at least one email address.',
        open: true,
        type: ToastType.error,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter((email) => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      setToastProps?.({
        message: 'Please enter valid email address(es).',
        open: true,
        type: ToastType.error,
      });
      return;
    }
    mutation.mutate({ emails: emailList });
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 400, fontSize: 34, pb: 0 }}>Invite your team</DialogTitle>
      <LinearProgress
        variant='determinate'
        value={(step / totalSteps) * 100}
        color='success'
        sx={{
          width: '100%',
          borderRadius: '24px 24px 0 0',
          height: 4,
          my: 1,
        }}
      />
      <Typography
        px={3}
        pt={1}
        fontSize={16}
        fontWeight={400}
        color={Theme.custom.colors.lightTextPrimary}
      >
        Add teammates to your trial so everyone can stay connected, aligned, and experience the
        benefits of Headlamp firsthand.
      </Typography>
      <DialogContent>
        <TextField
          multiline
          minRows={5}
          fullWidth
          placeholder='provider1@email.com, provider2@email.com'
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          sx={{
            mt: 1,
            mb: 2,
            background: '#fff',
            '& .MuiInputBase-input::placeholder': {
              color: Theme.custom.colors.lightTextSecondary,
              opacity: 1,
            },
          }}
        />
        <Box display='flex' justifyContent='flex-end' mt={2}>
          <Button
            variant='outlined'
            onClick={() => {
              onNext();
              trackMixpanelEvent(
                MixpanelEventName.FREE_TRIAL_INVITE_TEAM_SKIP_FOR_NOW_BUTTON_CLICKED,
              );
            }}
            sx={{ borderRadius: 8, px: 6, py: 1.5, mr: 2 }}
          >
            SKIP FOR NOW
          </Button>
          <Button
            variant='contained'
            onClick={handleSend}
            sx={{
              py: 1.5,
              px: 6,
              background: Theme.custom.colors.lightPrimaryMain,
              color: Theme.custom.colors.white,
              fontSize: 15,
              borderRadius: 8,
              boxShadow: 'none',
            }}
            disabled={mutation.isLoading}
          >
            SEND INVITES
          </Button>
        </Box>
      </DialogContent>
    </>
  );
}

export default InviteTeam;
